import { ListFaxesIO } from '../types';
import { UseFaxV1InfiniteQueryEndpointArgs, useFaxV1InfiniteQuery } from './use-fax-v1-infinite-query';

type ListFaxesPageParams = {
  lastId?: string;
  lastCreatedAt?: string;
};
/**
 * A hook that returns an infinite query for the `ListFaxes` endpoint.
 * @param request The request object to pass to the query. Overriding pagination fields on the request is handled by the hook.
 * @param options (optional) The options to pass to `useInfiniteQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListFaxesInfiniteQuery = <E = unknown, D = ListFaxesIO['output']>({
  options,
  request,
  ...args
}: UseFaxV1InfiniteQueryEndpointArgs<'ListFaxes', E, D, ListFaxesPageParams>) =>
  useFaxV1InfiniteQuery<'ListFaxes', E, D, ListFaxesPageParams>({
    endpointName: 'ListFaxes',
    ...args,
    request: ({ pageParam }) => {
      const requestWithPageParams: ListFaxesIO['input'] = {
        ...request,
      };
      if (pageParam?.lastCreatedAt) {
        requestWithPageParams.lastCreatedAt = pageParam.lastCreatedAt;
      }
      if (pageParam?.lastId) {
        requestWithPageParams.lastId = pageParam.lastId;
      }
      return requestWithPageParams;
    },
    options: {
      ...options,
      getNextPageParam: (lastPage) => {
        if (!lastPage.rows?.length) return undefined;
        const lastFax = lastPage.rows[lastPage.rows.length - 1];
        if (!lastFax) return undefined;
        return {
          lastCreatedAt: lastFax.createdAt,
          lastId: lastFax.id,
        };
      },
    },
  });
